/**
 * 프로젝트 아래 워크플로 폴더.
 *
 * 폴더는 별도 엔티티가 아니다 — 워크플로의 `folder` 경로('a/b')에서 **유추**한다.
 * 그래서 QBase 에는 컬럼 하나(FOLDER_PATH)만 더했고, 폴더 이름 변경·삭제는 그 아래 워크플로의
 * 경로를 고쳐 쓰는 일이다. 아직 워크플로가 없는 빈 폴더는 이 브라우저(localStorage)에만 기억해 둔다 —
 * 워크플로를 하나 넣는 순간부터는 서버에 남는다.
 */
import type { Workflow } from '../store/project/projectSlice';
import i18n from '../i18n';

export const SEP = '/';

/** 앞뒤 구분자·공백 정리. '' 이면 루트. */
export function normalizeFolder(path: string | undefined | null): string {
  return String(path || '')
    .replace(/\\/g, SEP)
    .split(SEP)
    .map((s) => s.trim())
    .filter(Boolean)
    .join(SEP);
}

export function folderName(path: string): string {
  const parts = normalizeFolder(path).split(SEP);
  return parts[parts.length - 1] || '';
}

export function parentFolder(path: string): string {
  const parts = normalizeFolder(path).split(SEP).filter(Boolean);
  return parts.slice(0, -1).join(SEP);
}

export function joinFolder(parent: string, name: string): string {
  return normalizeFolder([normalizeFolder(parent), name.trim()].filter(Boolean).join(SEP));
}

/** `path` 가 `folder` 자신이거나 그 아래인가 */
export function isInside(path: string | undefined, folder: string): boolean {
  const p = normalizeFolder(path);
  const f = normalizeFolder(folder);
  if (!f) return true;
  return p === f || p.startsWith(f + SEP);
}

/** 폴더 경로의 앞부분을 바꾼다 (이름 변경·이동에 쓴다). 안에 없으면 그대로. */
export function replacePrefix(path: string | undefined, from: string, to: string): string {
  const p = normalizeFolder(path);
  const f = normalizeFolder(from);
  const t = normalizeFolder(to);
  if (!isInside(p, f)) return p;
  const rest = p.slice(f.length).replace(/^\//, '');
  return normalizeFolder([t, rest].filter(Boolean).join(SEP));
}

/** 폴더 이름으로 쓸 수 없는 것 — 구분자·빈 문자열 */
export function validateFolderName(name: string): string | null {
  const n = name.trim();
  if (!n) return i18n.t('layout:folder.empty');
  if (n.includes(SEP) || n.includes('\\')) return i18n.t('layout:folder.sep', { sep: SEP });
  if (n.length > 100) return i18n.t('layout:folder.tooLong');
  return null;
}

/**
 * 이름 정렬 키 — ①…⑳ 같은 원문자 번호는 로케일 정렬이 숫자로 보지 않아 1, 10, 2 순으로 흩어진다.
 * 두 자리 숫자로 바꿔 두면 numeric 정렬이 1, 2, …, 10 으로 세운다.
 */
const CIRCLED = '①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳';
export function nameSortKey(name: string): string {
  return String(name || '').replace(/[①-⑳]/g, (ch) => String(CIRCLED.indexOf(ch) + 1).padStart(2, '0'));
}
export function compareNames(a: string, b: string): number {
  return nameSortKey(a).localeCompare(nameSortKey(b), 'ko', { numeric: true });
}

// ---------------------------------------------------------------- 트리

export interface FolderNode {
  /** 전체 경로 ('a/b'). 루트는 '' */
  path: string;
  name: string;
  children: FolderNode[];
  workflows: Workflow[];
  /** 이 폴더와 하위 폴더의 워크플로 수 */
  total: number;
}

/** 아직 한 번도 저장하지 않은 워크플로 (클라우드에 캔버스 버전이 없다) */
export const isUnsavedWorkflow = (w: Workflow): boolean => (w.curVerNo ?? 0) < 1;

/**
 * 워크플로 목록(+빈 폴더 목록) → 폴더 트리. 루트 노드의 path 는 ''.
 * 폴더·워크플로 모두 이름순으로 정렬하되, **아직 저장하지 않은 새 워크플로는 그 폴더 맨 위**에 둔다 —
 * 새로 만들면 이름순으로 목록 한가운데·맨 아래에 끼어 들어가 어디 갔는지 찾아야 했다(현장 2026-09-23).
 * 저장하면 제자리(이름순)로 간다.
 */
export function buildFolderTree(workflows: Workflow[], extraFolders: string[] = []): FolderNode {
  const root: FolderNode = { path: '', name: '', children: [], workflows: [], total: 0 };
  const byPath = new Map<string, FolderNode>([['', root]]);

  const ensure = (path: string): FolderNode => {
    const p = normalizeFolder(path);
    const hit = byPath.get(p);
    if (hit) return hit;
    const node: FolderNode = { path: p, name: folderName(p), children: [], workflows: [], total: 0 };
    byPath.set(p, node);
    ensure(parentFolder(p)).children.push(node);
    return node;
  };

  extraFolders.forEach((f) => { if (normalizeFolder(f)) ensure(f); });
  workflows.forEach((w) => { ensure(w.folder || '').workflows.push(w); });

  const finish = (n: FolderNode): number => {
    // numeric: 샘플①·샘플②…샘플⑩ 처럼 숫자가 섞인 이름이 1, 10, 2 순으로 흩어지지 않게
    n.children.sort((a, b) => compareNames(a.name, b.name));
    n.workflows.sort((a, b) => (Number(isUnsavedWorkflow(b)) - Number(isUnsavedWorkflow(a))) || compareNames(a.name, b.name));
    n.total = n.workflows.length + n.children.reduce((s, c) => s + finish(c), 0);
    return n.total;
  };
  finish(root);
  return root;
}

/** 트리에 있는 모든 폴더 경로 (루트 제외, 깊이 우선·이름순) — 폴더 선택 목록용 */
export function listFolderPaths(root: FolderNode): string[] {
  const out: string[] = [];
  const walk = (n: FolderNode) => { n.children.forEach((c) => { out.push(c.path); walk(c); }); };
  walk(root);
  return out;
}

// ---------------------------------------------------------------- 빈 폴더 (브라우저 기억)

const KEY = (projectId: string) => `openflow.folders.${projectId}`;

export function loadEmptyFolders(projectId: string): string[] {
  try {
    const raw = localStorage.getItem(KEY(projectId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.map(normalizeFolder).filter(Boolean) : [];
  } catch { return []; }
}

export function saveEmptyFolders(projectId: string, folders: string[]): void {
  try {
    const uniq = Array.from(new Set(folders.map(normalizeFolder).filter(Boolean)));
    if (uniq.length) localStorage.setItem(KEY(projectId), JSON.stringify(uniq));
    else localStorage.removeItem(KEY(projectId));
  } catch { /* 사생활 보호 모드 등 — 이번 세션만 기억한다 */ }
}
